import { isAxiosError } from "axios";

import { parseCommissionDebtError } from "@/lib/validation/commission.schemas";

export function isCommissionDebtLimitError(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 402) return false;

  return parseCommissionDebtError(error.response.data) !== null;
}

// Backend refuses online checkout for a store with no IBAN (no split possible)
// or for a store the owner has deactivated.
const CHECKOUT_UNAVAILABLE_MESSAGES = [
  "پرداخت آنلاین برای این فروشگاه هنوز فعال نشده است",
  "این فروشگاه در حال حاضر سفارش نمی‌پذیرد",
];

export function isOnlinePaymentDisabledError(error: unknown): boolean {
  if (!isAxiosError<{ message?: string }>(error) || error.response?.status !== 400) return false;

  return CHECKOUT_UNAVAILABLE_MESSAGES.includes(error.response.data?.message ?? "");
}

const PERSIAN_TEXT = /[؀-ۿ]/;

const isPersianMessage = (value: unknown): value is string =>
  typeof value === "string" && PERSIAN_TEXT.test(value);

/**
 * The backend's own Persian message for a failed request, or null. NestJS sends
 * validation failures as an array, so the first Persian entry is used. Validation
 * keys ("validation.is_number") and framework defaults ("Cannot POST …") are skipped.
 */
export function getApiErrorMessage(error: unknown): string | null {
  if (!isAxiosError<{ message?: unknown; message_fa?: unknown }>(error)) return null;
  const data = error.response?.data;
  if (isPersianMessage(data?.message_fa)) return data.message_fa;

  const message = data?.message;
  if (Array.isArray(message)) return message.find(isPersianMessage) ?? null;
  return isPersianMessage(message) ? message : null;
}

export type ApiErrorKind = "network" | "timeout" | "canceled" | "http" | "unknown";

export type NormalizedApiError = {
  kind: ApiErrorKind;
  status: number | null;
  /** Always a user-facing Persian message: the backend's own, or a default for the status. */
  message: string;
};

export const DEFAULT_ERROR_MESSAGE = "خطای غیرمنتظره‌ای رخ داد؛ دوباره تلاش کنید";

const KIND_MESSAGES: Record<Exclude<ApiErrorKind, "http">, string> = {
  network: "ارتباط با سرور برقرار نشد؛ اتصال اینترنت خود را بررسی کنید",
  timeout: "پاسخی از سرور دریافت نشد؛ دوباره تلاش کنید",
  canceled: "درخواست لغو شد",
  unknown: DEFAULT_ERROR_MESSAGE,
};

const STATUS_MESSAGES: Record<number, string> = {
  400: "اطلاعات ارسال‌شده معتبر نیست",
  401: "نشست شما به پایان رسیده است؛ دوباره وارد شوید",
  403: "شما به این بخش دسترسی ندارید",
  404: "مورد درخواستی یافت نشد",
  409: "این اطلاعات با داده‌های موجود تداخل دارد",
  413: "حجم فایل بیش از حد مجاز است",
  422: "اطلاعات ارسال‌شده معتبر نیست",
  429: "تعداد درخواست‌ها زیاد است؛ کمی بعد دوباره تلاش کنید",
};

const SERVER_ERROR_MESSAGE = "خطایی در سرور رخ داد؛ لطفاً کمی بعد دوباره تلاش کنید";

function getErrorKind(error: unknown): ApiErrorKind {
  if (!isAxiosError(error)) return "unknown";
  if (error.code === "ERR_CANCELED") return "canceled";
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return "timeout";
  if (error.response) return "http";
  return "network";
}

export function getHttpStatus(error: unknown): number | null {
  return isAxiosError(error) ? (error.response?.status ?? null) : null;
}

export function normalizeApiError(error: unknown, fallback?: string): NormalizedApiError {
  const kind = getErrorKind(error);
  const status = getHttpStatus(error);

  if (kind !== "http" || status === null) {
    return { kind, status, message: KIND_MESSAGES[kind === "http" ? "unknown" : kind] };
  }

  const message =
    getApiErrorMessage(error) ??
    fallback ??
    STATUS_MESSAGES[status] ??
    (status >= 500 ? SERVER_ERROR_MESSAGE : DEFAULT_ERROR_MESSAGE);

  return { kind, status, message };
}

/**
 * A user-facing message for any thrown value. `fallback` replaces the generic
 * per-status text when the backend sent no Persian message of its own; network
 * and timeout failures keep their own text since a fallback would mislead there.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  return normalizeApiError(error, fallback).message;
}

/** Network blips, timeouts and 5xx responses are worth retrying; 4xx never are. */
export function isRetryableError(error: unknown): boolean {
  const { kind, status } = normalizeApiError(error);
  if (kind === "network" || kind === "timeout") return true;
  return status !== null && status >= 500 && status !== 501;
}
